import {
  DianaDataType,
  getNextPageParam,
  IFlow,
  IProperty,
  IWork,
  PageViewModeEnum,
  performanceGraphqlFlattenWorks,
  performanceWorkQuery,
  useAbortRequestWhenFiltersValuesChanged,
  useDataExplorerReportStore,
  useEnvironment,
  usePerformanceGraphQLParameters,
  useServerAggregatedColumnOperation,
  useTableSort,
  useWorkVersionDrawer,
} from '@diana/core';
import { Grid } from '@mui/material';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useInfiniteQuery } from '@tanstack/react-query';
import { forwardRef, Fragment } from 'react';
import { DataExplorerReportBoard } from './DataExplorerReportBoard';
import { DataExplorerReportTable } from './DataExplorerReportTable';
import { getDataExplorerGraphQLWorksQueryKey } from './Support';

type ContentProps = {
  flows: IFlow[];
  forceEmptySelection: boolean;
  supportedProperties: IProperty[];
};

export const Content = forwardRef<GridApiPremium, ContentProps>((props, forwardedRef) => {
  const { flows, forceEmptySelection, supportedProperties } = props;
  const environment = useEnvironment();
  const viewMode = useDataExplorerReportStore((store) => store.viewMode);
  const filtersValues = useDataExplorerReportStore((store) => store.filtersValues);
  const availableFilters = useDataExplorerReportStore((store) => store.availableFilters);

  const { sortModel, onSortModelChange } = useTableSort({ supportedProperties });

  const { abort } = useAbortRequestWhenFiltersValuesChanged({
    filtersValues,
    sortModel,
    abortOnUnmount: false,
  });

  const { sortField, sortDirection, ...queryParameters } = usePerformanceGraphQLParameters({
    flows,
    filters: availableFilters,
    filtersValues,
    abort,
    showCompleted: true,
    showDeleted: true,
    showInProgress: true,
    sortModel,
  });

  useWorkVersionDrawer();

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<IWork[]>({
      queryKey: getDataExplorerGraphQLWorksQueryKey(queryParameters.filterProperties, sortModel),
      queryFn: async ({ pageParam }) => {
        return await performanceWorkQuery.get(queryParameters).fn({
          skip: pageParam as number,
          sortField,
          sortDirection,
        });
      },
      enabled: !!flows.length,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 0,
      getNextPageParam: getNextPageParam,
    });

  const works = performanceGraphqlFlattenWorks(data).map((work) => {
    const cardLayout = flows.find((flow) => flow.flowOriginId === work.flowOriginId)?.cardLayout;
    if (cardLayout) {
      return { ...work, cardLayout };
    }
    return work;
  });

  const aggregateColumnOperation = useServerAggregatedColumnOperation({
    filters: availableFilters,
    filtersValues,
    properties: supportedProperties,
    dianaDataType: DianaDataType.Work,
    showCompleted: queryParameters.showCompleted,
    showDeleted: queryParameters.showDeleted,
    showInProgress: queryParameters.showInProgress,
    dateFrom: queryParameters.dateFrom,
    dateTo: queryParameters.dateTo,
    flows,
  });

  return (
    <Fragment>
      {viewMode === PageViewModeEnum.Board ? (
        <DataExplorerReportBoard
          environment={environment}
          isLoading={isLoading}
          works={works}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      ) : (
        <Grid>
          <DataExplorerReportTable
            ref={forwardedRef}
            works={works}
            isLoading={isFetching}
            forceEmptySelection={forceEmptySelection}
            supportedProperties={supportedProperties}
            fetchNextPage={fetchNextPage}
            onSortModelChange={onSortModelChange}
            aggregateColumnOperation={aggregateColumnOperation}
          />
        </Grid>
      )}
    </Fragment>
  );
});
